import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: February 2026</p>

        <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Who We Are</h2>
            <p>
              This website (<strong>brantfordareahomes.ca</strong>) is operated by <strong>Alexander Conti</strong>,
              Sales Representative &mdash; REALTOR® with{' '}
              <strong>Royal LePage Brant Realty, Brokerage</strong>. Throughout this policy, &quot;we&quot;, &quot;us&quot;, and &quot;our&quot;
              refer to Alexander Conti and Royal LePage Brant Realty, Brokerage.
            </p>
            <p>
              We are committed to protecting your privacy and the personal information you share with us, in
              accordance with Canada's <em>Personal Information Protection and Electronic Documents Act (PIPEDA)</em>,{' '}
              Ontario's <em>Trust in Real Estate Services Act, 2002 (TRESA)</em>, and the requirements of the{' '}
              <em>Real Estate Council of Ontario (RECO)</em>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. What Information We Collect</h2>
            <p>We collect the following types of information from visitors and clients:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Contact information:</strong> name, email address, phone number, mailing address</li>
              <li><strong>Property preferences:</strong> areas of interest, price range, must-haves, timeline</li>
              <li><strong>Chat and call interactions:</strong> messages exchanged with our website chat assistant, transcripts of phone calls placed or received through our system (these calls may be recorded for quality and follow-up)</li>
              <li><strong>Listing activity:</strong> properties you save, favorite, or schedule showings for</li>
              <li><strong>Website usage:</strong> IP address, browser type, pages visited, referring URL, time of visit (via standard web analytics)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. How We Use Your Information</h2>
            <p>We use your information only for purposes you would reasonably expect, including:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Responding to your inquiries about properties, neighborhoods, financing, or our services</li>
              <li>Sending you property listings, market updates, and follow-up communications you have opted into</li>
              <li>Scheduling appointments, showings, and meetings via email, phone, or Google Calendar invites</li>
              <li>Improving our website experience and the relevance of property matches</li>
              <li>Meeting our legal, regulatory, and brokerage compliance obligations</li>
            </ul>
            <p>
              You can unsubscribe from non-essential communications at any time by clicking the unsubscribe link in
              any email, replying STOP to any text, or visiting{' '}
              <a href="/email-preferences" className="text-red-600 hover:underline">your preferences page</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. AI-Assisted Communications</h2>
            <p>
              Our website uses AI-assisted tools (chat assistant and voice assistant named "Lauren") to help respond
              quickly to inquiries on behalf of Alexander Conti. These tools may:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Send and receive text messages with you</li>
              <li>Place outbound phone calls on our behalf to follow up on inquiries</li>
              <li>Send emails on our behalf</li>
              <li>Pass your conversation contents to Alexander for follow-up</li>
            </ul>
            <p>
              All AI-assisted communications are subject to this privacy policy. Phone calls placed or received
              through our system may be recorded for quality, training, and lead follow-up purposes. By engaging
              with our chat widget or accepting a phone call from our system, you consent to this recording. You
              may request a copy of any recording or transcript involving you by contacting us at the details below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Who We Share Your Information With</h2>
            <p>We do not sell or rent your personal information. We share your information only with:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Our brokerage:</strong> Royal LePage Brant Realty, Brokerage, for transaction and compliance purposes</li>
              <li><strong>Service providers</strong> who help us operate our website, communications, and CRM, including: KVcore (CRM), Twilio (SMS and voice), ElevenLabs (text-to-speech), Anthropic Claude (AI assistant), Google (Calendar, OAuth), Resend (transactional email)</li>
              <li><strong>Other professionals you authorize</strong> in your transaction (mortgage broker, lawyer, home inspector, etc.)</li>
              <li><strong>Government or regulatory bodies</strong> when required by law or by RECO</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Cookies and Tracking</h2>
            <p>
              Our website uses cookies and similar technologies to keep you signed in, remember your favorites,
              improve performance, and measure traffic. You can disable cookies in your browser, but some features
              of the site may not work properly. We do not use cookies to track you across other websites.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. How We Protect Your Information</h2>
            <p>
              We use industry-standard security measures including TLS encryption for all data in transit, encrypted
              databases for stored personal information, and role-based access controls so only authorized team
              members can see your information. We retain your information only as long as needed for the purposes
              described above or as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. Your Rights</h2>
            <p>Under PIPEDA, you have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Access the personal information we hold about you</li>
              <li>Correct any inaccurate information</li>
              <li>Withdraw your consent and request deletion (subject to legal/regulatory retention requirements)</li>
              <li>File a complaint with the Office of the Privacy Commissioner of Canada at <a href="https://www.priv.gc.ca" className="text-red-600 hover:underline" target="_blank" rel="noopener noreferrer">priv.gc.ca</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Contact Us</h2>
            <p>To exercise any of your rights, ask a privacy question, or file a concern:</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mt-3">
              <p className="font-semibold text-gray-900">Alexander Conti</p>
              <p>Sales Representative &mdash; REALTOR®</p>
              <p>Royal LePage Brant Realty, Brokerage</p>
              <p className="mt-2">📧 <a href="mailto:alexanderconti@brantfordareahomes.ca" className="text-red-600 hover:underline">alexanderconti@brantfordareahomes.ca</a></p>
              <p>📞 <a href="tel:+12269344107" className="text-red-600 hover:underline">226-934-4107</a></p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last updated" date at the top of this page
              indicates when the most recent change was made. We encourage you to review this policy periodically.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
